import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Package, AlertTriangle, Layers } from 'lucide-react';
import { useTranslation } from '../../../contexts/I18nProvider';
import {
    BinLocation,
    BinLocationGroup,
    RackStructure,
    compareSegments,
    CellColor,
    GridDensity,
    BinDescriptionResolvers
} from './types';

/**
 * RackGridCore — lag-basert reol-grid (RackGridV2)
 *
 * Ren visnings-/seleksjonskomponent: all datahenting skjer hos forelderen og
 * kommer inn som props (farge-lag via cellColor, ekstra celleinnhold via cellExtra).
 *
 * - X-akse: verticals (V01, V02, ...) venstre → høyre
 * - Y-akse: hyller (H09 ... H00) øverst → nederst
 * - Excel-seleksjon: klikk, Ctrl+klikk, Shift+klikk, dra — portert uendret fra
 *   rack-builder/RackGrid (4D-celler velger alle skuffer samlet)
 * - Tetthet S/M/L, søke-highlight + scroll-til-treff, klikk-callback for detaljpanel
 */

interface RackGridCoreProps {
    rackStructure: RackStructure;
    selectedBinCodes: string[];
    onSelectionChange: (binCodes: string[]) => void;
    filteredBinCodes: string[];
    /** Celletetthet (default 'L' = dagens visning) */
    density?: GridDensity;
    /** Aktivt farge-lag: returner farge for lokasjonen, eller null for standard styling */
    cellColor?: (location: BinLocation) => CellColor | null;
    /** Ekstra innholdslinje i cellen (M/L), f.eks. saldo — rendres under bincode */
    cellExtra?: (group: BinLocationGroup) => React.ReactNode;
    /** Lokasjoner som skal søke-highlightes (oransje ramme) */
    highlightedBinCodes?: Set<string>;
    /** Kalles ved normal-klikk på en celle (etter seleksjonslogikken) — for detaljpanel */
    onLocationClick?: (group: BinLocationGroup) => void;
    /** Scroll cellen med denne bincoden inn i synsfeltet (settes ved søk/hopp) */
    scrollToBinCode?: string | null;
    /** Lesbare beskrivelser til tooltip (valgfritt — uten vises rå verdier) */
    describe?: BinDescriptionResolvers;
    /** Skjul footer (legend + seleksjonsinfo + instruksjoner) — brukes når forelderen har egen */
    hideFooter?: boolean;
    /** false = ren visning: ingen seleksjon/dra (heatmap-visningsmodus). Default true. */
    selectable?: boolean;
    /** false = skjul konfig-innhold i L-celler (ikoner + størrelseslinje) — for aktivitets-/saldolag. Default true. */
    showConfigInfo?: boolean;
    /** Overstyr tooltip-innholdet helt (f.eks. aktivitetsstatistikk i heatmap) */
    renderTooltip?: (location: BinLocation, group?: BinLocationGroup) => React.ReactNode;
}

interface CellPosition {
    vertical: string;
    shelf: string;
}

/** Dimensjonsklasser per tetthet. Lik bredde på celle og kolonneheader er påkrevd. */
const DENSITY_CLASSES: Record<GridDensity, { cell: string; header: string; labelCol: string; font: string }> = {
    S: { cell: 'w-9 h-9', header: 'w-9 text-[10px]', labelCol: 'w-12 text-[10px]', font: 'text-[9px]' },
    M: { cell: 'w-24 h-16', header: 'w-24 text-xs', labelCol: 'w-14 text-xs', font: 'text-[10px]' },
    L: { cell: 'w-36 h-28', header: 'w-36 text-base', labelCol: 'w-20 text-base', font: 'text-sm' }
};

export const RackGridCore: React.FC<RackGridCoreProps> = ({
    rackStructure,
    selectedBinCodes,
    onSelectionChange,
    filteredBinCodes,
    density = 'L',
    cellColor,
    cellExtra,
    highlightedBinCodes,
    onLocationClick,
    scrollToBinCode,
    describe,
    hideFooter = false,
    selectable = true,
    showConfigInfo = true,
    renderTooltip
}) => {
    const { t } = useTranslation();
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<CellPosition | null>(null);
    const [dragCurrent, setDragCurrent] = useState<CellPosition | null>(null);
    const [hoveredLocation, setHoveredLocation] = useState<BinLocation | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    const lastClickedRef = useRef<string | null>(null);

    const dims = DENSITY_CLASSES[density];

    // Set-baserte oppslag (O(1) per celle)
    const selectedSet = useMemo(() => new Set(selectedBinCodes), [selectedBinCodes]);
    const filteredSet = useMemo(() => new Set(filteredBinCodes), [filteredBinCodes]);

    // "vertical-shelf" -> primær BinLocation
    const locationMap = useMemo(() => {
        const map = new Map<string, BinLocation>();
        rackStructure.locations.forEach((loc: BinLocation) => {
            const key = `${loc.vertical}-${loc.shelf}`;
            if (!map.has(key)) {
                map.set(key, loc);
            }
        });
        return map;
    }, [rackStructure.locations]);

    // Grupper per celle for 4D-skuffer
    const locationGroups = useMemo(() => {
        const groups = new Map<string, BinLocationGroup>();
        rackStructure.locations.forEach((loc: BinLocation) => {
            const cellKey = `${loc.vertical}-${loc.shelf}`;
            if (!groups.has(cellKey)) {
                groups.set(cellKey, {
                    cellKey,
                    rackNumber: loc.rackNumber,
                    vertical: loc.vertical,
                    shelf: loc.shelf,
                    locations: [],
                    primaryLocation: loc,
                    hasMultipleDrawers: false,
                    drawerCount: 0
                });
            }
            const group = groups.get(cellKey)!;
            group.locations.push(loc);
            group.drawerCount = group.locations.length;
            group.hasMultipleDrawers = group.drawerCount > 1;
            group.locations.sort((a, b) => compareSegments(a.drawer ?? '', b.drawer ?? ''));
            group.primaryLocation = group.locations[0];
        });
        return groups;
    }, [rackStructure.locations]);

    const getAllBinCodesForCell = (vertical: string, shelf: string): string[] => {
        const group = locationGroups.get(`${vertical}-${shelf}`);
        return group ? group.locations.map(loc => loc.binCode) : [];
    };

    const sortedShelves = useMemo(
        () => [...rackStructure.shelves].sort((a, b) => compareSegments(b, a)),
        [rackStructure.shelves]
    );

    const sortedVerticals = useMemo(
        () => [...rackStructure.verticals].sort(compareSegments),
        [rackStructure.verticals]
    );

    const isSelectable = (binCode: string): boolean => filteredSet.has(binCode);
    const isSelected = (binCode: string): boolean => selectedSet.has(binCode);

    const getLocationAt = (vertical: string, shelf: string): BinLocation | undefined =>
        locationMap.get(`${vertical}-${shelf}`);

    /** Alle lokasjoner i et rektangulært område (indeks-basert for non-numeriske segmenter) */
    const getLocationsInRange = (start: CellPosition, end: CellPosition): string[] => {
        const vIdxStart = sortedVerticals.indexOf(start.vertical);
        const vIdxEnd = sortedVerticals.indexOf(end.vertical);
        const sIdxStart = sortedShelves.indexOf(start.shelf);
        const sIdxEnd = sortedShelves.indexOf(end.shelf);
        if (vIdxStart < 0 || vIdxEnd < 0 || sIdxStart < 0 || sIdxEnd < 0) return [];

        const minV = Math.min(vIdxStart, vIdxEnd);
        const maxV = Math.max(vIdxStart, vIdxEnd);
        const minS = Math.min(sIdxStart, sIdxEnd);
        const maxS = Math.max(sIdxStart, sIdxEnd);

        const locations: string[] = [];
        for (let v = minV; v <= maxV; v++) {
            for (let s = minS; s <= maxS; s++) {
                const loc = getLocationAt(sortedVerticals[v], sortedShelves[s]);
                if (loc && isSelectable(loc.binCode)) {
                    locations.push(loc.binCode);
                }
            }
        }
        return locations;
    };

    const handleCellClick = (location: BinLocation, event: React.MouseEvent) => {
        if (!selectable) {
            const group = locationGroups.get(`${location.vertical}-${location.shelf}`);
            if (group && onLocationClick) onLocationClick(group);
            return;
        }
        const cellBinCodes = getAllBinCodesForCell(location.vertical, location.shelf);
        const selectableCodes = cellBinCodes.filter(code => isSelectable(code));
        if (selectableCodes.length === 0) return;

        const isCtrlKey = event.ctrlKey || event.metaKey;
        const isShiftKey = event.shiftKey;

        if (isShiftKey && lastClickedRef.current) {
            const lastLoc = rackStructure.locations.find((l: BinLocation) => l.binCode === lastClickedRef.current);
            if (lastLoc) {
                const start = { vertical: lastLoc.vertical, shelf: lastLoc.shelf };
                const end = { vertical: location.vertical, shelf: location.shelf };
                const rangeLocations = getLocationsInRange(start, end);
                onSelectionChange(Array.from(new Set([...selectedBinCodes, ...rangeLocations])));
            }
        } else if (isCtrlKey) {
            const allSelected = selectableCodes.every(code => selectedSet.has(code));
            if (allSelected) {
                const cellCodeSet = new Set(selectableCodes);
                onSelectionChange(selectedBinCodes.filter(code => !cellCodeSet.has(code)));
            } else {
                onSelectionChange(Array.from(new Set([...selectedBinCodes, ...selectableCodes])));
            }
            lastClickedRef.current = location.binCode;
        } else {
            onSelectionChange(selectableCodes);
            lastClickedRef.current = location.binCode;
            const group = locationGroups.get(`${location.vertical}-${location.shelf}`);
            if (group && onLocationClick) {
                onLocationClick(group);
            }
        }
    };

    const handleDragStart = (vertical: string, shelf: string, event: React.MouseEvent) => {
        if (!selectable) return;
        const location = getLocationAt(vertical, shelf);
        if (!location || !isSelectable(location.binCode)) return;
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return;

        event.preventDefault();
        setIsDragging(true);
        setDragStart({ vertical, shelf });
        setDragCurrent({ vertical, shelf });
        onSelectionChange([location.binCode]);
        lastClickedRef.current = location.binCode;
    };

    const handleDragMove = (vertical: string, shelf: string) => {
        if (!isDragging || !dragStart) return;
        setDragCurrent({ vertical, shelf });
        onSelectionChange(getLocationsInRange(dragStart, { vertical, shelf }));
    };

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        setDragStart(null);
        setDragCurrent(null);
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mouseup', handleDragEnd);
            return () => window.removeEventListener('mouseup', handleDragEnd);
        }
    }, [isDragging, handleDragEnd]);

    // Scroll til søketreff
    useEffect(() => {
        if (!scrollToBinCode || !gridRef.current) return;
        const cell = gridRef.current.querySelector(`[data-bincode="${CSS.escape(scrollToBinCode)}"]`);
        if (cell) {
            cell.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
    }, [scrollToBinCode]);

    const handleCellHover = (location: BinLocation, event: React.MouseEvent) => {
        setHoveredLocation(location);
        setTooltipPosition({ x: event.clientX, y: event.clientY });
    };

    const handleCellLeave = () => {
        setHoveredLocation(null);
        setTooltipPosition(null);
    };

    const isInDragSelection = (vertical: string, shelf: string): boolean => {
        if (!isDragging || !dragStart || !dragCurrent) return false;
        const vIdx = sortedVerticals.indexOf(vertical);
        const sIdx = sortedShelves.indexOf(shelf);
        const vStart = sortedVerticals.indexOf(dragStart.vertical);
        const vEnd = sortedVerticals.indexOf(dragCurrent.vertical);
        const sStart = sortedShelves.indexOf(dragStart.shelf);
        const sEnd = sortedShelves.indexOf(dragCurrent.shelf);
        if (vIdx < 0 || sIdx < 0 || vStart < 0 || vEnd < 0 || sStart < 0 || sEnd < 0) return false;
        return vIdx >= Math.min(vStart, vEnd) && vIdx <= Math.max(vStart, vEnd)
            && sIdx >= Math.min(sStart, sEnd) && sIdx <= Math.max(sStart, sEnd);
    };

    /** Klasser + inline-style for en celle, med lagfarge og seleksjons-/highlight-tilstand */
    const getCellPresentation = (
        location: BinLocation | undefined,
        vertical: string,
        shelf: string
    ): { className: string; style: React.CSSProperties } => {
        if (!location) {
            return { className: 'bg-gray-50 border border-dashed border-gray-200', style: {} };
        }

        const selectable = isSelectable(location.binCode);
        const selected = isSelected(location.binCode);
        const inDrag = isInDragSelection(vertical, shelf);
        const highlighted = highlightedBinCodes?.has(location.binCode) ?? false;

        const baseClasses = 'relative border transition-all cursor-pointer select-none';
        const style: React.CSSProperties = {};

        if (!selectable) {
            return {
                className: `${baseClasses} bg-gray-100 border-dashed border-gray-300 opacity-50 pointer-events-none`,
                style
            };
        }

        const layer = cellColor ? cellColor(location) : null;
        let className = baseClasses;

        if (layer) {
            style.backgroundColor = layer.background;
            style.borderColor = layer.border ?? layer.background;
            if (layer.text) style.color = layer.text;
        } else {
            className += ' bg-white border-gray-300';
        }

        if (selected || inDrag) {
            className += ' border-blue-600 border-2 shadow-sm';
            // Behold lagfargen som bakgrunn, men marker tydelig med blå ramme
            style.borderColor = '#2563eb';
            if (!layer) {
                className = className.replace('bg-white', 'bg-blue-100');
            }
        } else if (!layer) {
            className += ' hover:border-blue-400 hover:shadow';
        }

        if (highlighted) {
            // Inline outline i stedet for Tailwind-ring: identisk i alle tre temaer
            style.outline = '3px solid #f59e0b';
            style.outlineOffset = '-1px';
        }

        return { className, style };
    };

    if (rackStructure.locations.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                <p>{t('manager.modules.rackBuilder.grid.noLocationsToShow', {}, 'Ingen lokasjoner å vise')}</p>
            </div>
        );
    }

    const spacing = density === 'S' ? 'space-y-0.5' : 'space-y-1.5';
    const cellGapPad = density === 'S' ? 'p-0.5' : 'p-2';

    return (
        <div className="w-full h-full flex flex-col">
            {/* Grid container */}
            <div
                ref={gridRef}
                className={`w-full h-full border border-gray-300 bg-white rounded-lg overflow-auto ${density === 'S' ? 'p-2' : 'p-4'}`}
                style={{ userSelect: 'none' }}
            >
                {/* Kolonneheader (verticals) - sticky */}
                <div className={`flex ${density === 'S' ? 'mb-1' : 'mb-3'} sticky top-0 bg-white z-10 border-b border-gray-200 pb-1`}>
                    <div className={`${dims.labelCol.split(' ')[0]} flex-shrink-0`} />
                    {sortedVerticals.map(vertical => (
                        <div
                            key={vertical}
                            className={`${dims.header} text-center font-semibold text-gray-700 flex-shrink-0`}
                        >
                            V{/^\d+$/.test(vertical) ? vertical.padStart(2, '0') : vertical}
                        </div>
                    ))}
                </div>

                {/* Reol-matrisen */}
                <div className={spacing}>
                    {sortedShelves.map((shelf) => (
                        <div key={shelf} className="flex items-center">
                            {/* Hyllelabel - sticky */}
                            <div className={`${dims.labelCol} text-right pr-2 font-semibold text-gray-700 flex-shrink-0 sticky left-0 bg-white z-10`}>
                                H{/^\d+$/.test(shelf) ? shelf.padStart(2, '0') : shelf}
                            </div>

                            {sortedVerticals.map(vertical => {
                                const location = getLocationAt(vertical, shelf);
                                const cellKey = `${vertical}-${shelf}`;
                                const group = locationGroups.get(cellKey);
                                const { className: cellClass, style: cellStyle } = getCellPresentation(location, vertical, shelf);

                                return (
                                    <div
                                        key={cellKey}
                                        data-bincode={location?.binCode}
                                        className={`${dims.cell} ${cellClass} flex flex-col items-center justify-center ${cellGapPad} ${dims.font} flex-shrink-0 overflow-hidden`}
                                        style={cellStyle}
                                        onClick={(e) => location && handleCellClick(location, e)}
                                        onMouseDown={(e) => handleDragStart(vertical, shelf, e)}
                                        onMouseEnter={() => {
                                            if (isDragging) {
                                                handleDragMove(vertical, shelf);
                                            }
                                        }}
                                        onMouseMove={(e) => location && handleCellHover(location, e)}
                                        onMouseLeave={handleCellLeave}
                                    >
                                        {location ? (
                                            density === 'S' ? (
                                                <>
                                                    {/* Kompakt: kun indikatorer — bincode i tooltip/detaljpanel */}
                                                    {location.itemNo && (
                                                        <div className="w-1.5 h-1.5 rounded-full bg-green-600" />
                                                    )}
                                                    {group?.hasMultipleDrawers && (
                                                        <span className="text-[8px] font-semibold text-purple-700">{group.drawerCount}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {/* BinCode (primær) */}
                                                    <div className={`font-mono font-semibold ${density === 'M' ? 'text-[10px]' : 'text-sm'} truncate max-w-full`}>
                                                        {group?.primaryLocation.binCode ?? location.binCode}
                                                    </div>

                                                    {/* 4D-skuffindikator */}
                                                    {group?.hasMultipleDrawers && (
                                                        <div
                                                            className={`flex items-center justify-center bg-purple-100 border border-purple-300 rounded px-1 py-0.5 ${density === 'L' ? 'mt-1' : 'mt-0.5'}`}
                                                            title={t('manager.modules.rackBuilder.grid.drawersInLocation', { '0': group.drawerCount }, '{0} skuffer i denne lokasjonen')}
                                                        >
                                                            <Layers className="w-3 h-3 text-purple-600" />
                                                            <span className="text-xs font-semibold text-purple-700 ml-1">
                                                                {group.drawerCount}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Ekstra innholdslinje fra aktivt lag (f.eks. saldo) */}
                                                    {cellExtra && group && (
                                                        <div className={`${density === 'M' ? 'text-[9px]' : 'text-xs'} truncate max-w-full`}>
                                                            {cellExtra(group)}
                                                        </div>
                                                    )}

                                                    {density === 'L' && showConfigInfo && (
                                                        <>
                                                            {/* Indikatorer */}
                                                            <div className="flex items-center justify-center space-x-1.5 mt-1 h-5">
                                                                {location.itemNo && (
                                                                    <div title={t('manager.modules.rackBuilder.grid.dedicatedLocation', {}, 'Dedikert lokasjon')}>
                                                                        <Package className="w-4 h-4 text-green-600" />
                                                                    </div>
                                                                )}
                                                                {location.pickPriorityNo && location.pickPriorityNo > 50 && (
                                                                    <div title={t('manager.modules.rackBuilder.grid.lowPriority', {}, 'Lav prioritet')}>
                                                                        <AlertTriangle className="w-4 h-4 text-yellow-600" />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Størrelse/type — lesbar beskrivelse når tilgjengelig */}
                                                            <div className="text-xs text-gray-500 mt-0.5 truncate max-w-full" style={cellStyle.color ? { color: cellStyle.color, opacity: 0.75 } : undefined}>
                                                                {describe
                                                                    ? describe.binSize(location.binSizeNo)
                                                                    : `${location.binSizeNo}/${location.binTypeNo}`}
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            )
                                        ) : (
                                            <div className="text-gray-400 text-[8px]">-</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {/* Tooltip */}
            {hoveredLocation && tooltipPosition && (() => {
                const hoveredGroup = locationGroups.get(`${hoveredLocation.vertical}-${hoveredLocation.shelf}`);

                // Egendefinert tooltip (f.eks. aktivitetsstatistikk) erstatter standardinnholdet
                if (renderTooltip) {
                    const content = renderTooltip(hoveredLocation, hoveredGroup);
                    if (!content) return null;
                    return (
                        <div
                            className="fixed z-50 bg-gray-900 text-white p-3 rounded shadow-lg text-xs max-w-xs"
                            style={{
                                left: tooltipPosition.x + 10,
                                top: tooltipPosition.y + 10,
                                pointerEvents: 'none'
                            }}
                        >
                            {content}
                        </div>
                    );
                }

                return (
                    <div
                        className="fixed z-50 bg-gray-900 text-white p-3 rounded shadow-lg text-xs max-w-xs"
                        style={{
                            left: tooltipPosition.x + 10,
                            top: tooltipPosition.y + 10,
                            pointerEvents: 'none'
                        }}
                    >
                        <div className="font-semibold mb-1">{hoveredLocation.binCode}</div>
                        {hoveredLocation.description1 && (
                            <div className="mb-1">{hoveredLocation.description1}</div>
                        )}

                        {hoveredGroup?.hasMultipleDrawers && (
                            <div className="mb-2 pb-2 border-b border-gray-700">
                                <div className="text-purple-300 font-semibold mb-1">
                                    📦 {t('manager.modules.rackBuilder.grid.drawersInCell', { '0': hoveredGroup.drawerCount }, '{0} skuffer i denne cellen:')}
                                </div>
                                <div className="space-y-0.5 max-h-32 overflow-y-auto">
                                    {hoveredGroup.locations.map((loc) => (
                                        <div key={loc.binCode} className="text-gray-400 text-[10px] font-mono">
                                            {loc.binCode} {loc.drawer !== undefined && t('manager.modules.rackBuilder.grid.drawerParen', { '0': loc.drawer }, '(Skuff {0})')}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="space-y-0.5 text-gray-300">
                            <div>{t('manager.modules.rackBuilder.grid.tooltipSize', { '0': describe ? describe.binSize(hoveredLocation.binSizeNo) : hoveredLocation.binSizeNo }, 'Størrelse: {0}')}</div>
                            <div>{t('manager.modules.rackBuilder.grid.tooltipType', { '0': describe ? describe.binType(hoveredLocation.binTypeNo) : hoveredLocation.binTypeNo }, 'Type: {0}')}</div>
                            {hoveredLocation.resourceNo && (
                                <div>{t('manager.modules.rackBuilder.grid.tooltipResource', { '0': describe ? describe.resource(hoveredLocation.resourceNo) : hoveredLocation.resourceNo }, 'Ressurs: {0}')}</div>
                            )}
                            {hoveredLocation.stcZoneID && (
                                <div>{t('manager.modules.rackBuilder.grid.tooltipZone', { '0': describe ? describe.zone(hoveredLocation.stcZoneID) : hoveredLocation.stcZoneID }, 'Sone: {0}')}</div>
                            )}
                            {hoveredLocation.pickPriorityNo && (
                                <div>{t('manager.modules.rackBuilder.grid.tooltipPickPriority', { '0': describe ? describe.priority(hoveredLocation.pickPriorityNo) : hoveredLocation.pickPriorityNo }, 'Plukkprioritet: {0}')}</div>
                            )}
                            {hoveredLocation.itemNo && (
                                <div className="text-green-300 font-semibold">
                                    {t('manager.modules.rackBuilder.grid.tooltipDedicatedTo', { '0': hoveredLocation.itemNo }, 'Dedikert til: {0}')}
                                </div>
                            )}
                            <div>
                                {t('manager.modules.rackBuilder.grid.tooltipPickable', { '0': hoveredLocation.pickableYesNo ? t('manager.modules.rackBuilder.common.yes', {}, 'Ja') : t('manager.modules.rackBuilder.common.no', {}, 'Nei') }, 'Plukkbar: {0}')}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {!hideFooter && (
                <>
                    {/* Footer: basis-legend + seleksjonsinfo */}
                    <div className="mt-2 flex items-center justify-between text-xs gap-4">
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-1.5">
                                <div className="w-3 h-3 bg-white border border-gray-300 rounded" />
                                <span>{t('manager.modules.rackBuilder.grid.legendNormal', {}, 'Normal')}</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                                <div className="w-3 h-3 bg-blue-100 border-2 border-blue-500 rounded" />
                                <span>{t('manager.modules.rackBuilder.grid.legendSelected', {}, 'Valgt')}</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                                <div className="w-3 h-3 bg-gray-100 border border-dashed border-gray-300 rounded opacity-50" />
                                <span>{t('manager.modules.rackBuilder.grid.legendFiltered', {}, 'Filtrert')}</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                                <Package className="w-3 h-3 text-green-600" />
                                <span>{t('manager.modules.rackBuilder.grid.legendDedicated', {}, 'Dedikert')}</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                                <div className="flex items-center bg-purple-100 border border-purple-300 rounded px-1 py-0.5">
                                    <Layers className="w-3 h-3 text-purple-600" />
                                    <span className="text-[10px] font-semibold text-purple-700 ml-0.5">3</span>
                                </div>
                                <span>{t('manager.modules.rackBuilder.grid.legend4D', {}, '4D (skuffer)')}</span>
                            </div>
                        </div>

                        <div className="text-gray-600 whitespace-nowrap">
                            <span className="font-semibold">
                                {selectedBinCodes.length}/{filteredBinCodes.length}
                            </span>
                            {isDragging && (
                                <span className="ml-2 text-blue-600 font-semibold">
                                    {t('manager.modules.rackBuilder.grid.dragging', {}, '(Dra)')}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="mt-1 text-xs text-gray-500">
                        💡 <strong>{t('manager.modules.rackBuilder.grid.instrClick', {}, 'Klikk')}</strong>={t('manager.modules.rackBuilder.grid.instrClickAction', {}, 'velg')} | <strong>{t('manager.modules.rackBuilder.grid.instrCtrlClick', {}, 'Ctrl+Klikk')}</strong>={t('manager.modules.rackBuilder.grid.instrCtrlClickAction', {}, 'flere')} | <strong>{t('manager.modules.rackBuilder.grid.instrShiftClick', {}, 'Shift+Klikk')}</strong>={t('manager.modules.rackBuilder.grid.instrShiftClickAction', {}, 'område')} | <strong>{t('manager.modules.rackBuilder.grid.instrDrag', {}, 'Dra')}</strong>={t('manager.modules.rackBuilder.grid.instrDragAction', {}, 'Excel')}
                    </div>
                </>
            )}
        </div>
    );
};
